import {useRef} from "react";

import {hitUserSegmentLocationEntry} from "@pg-mono/google-tag-manager";
import {isEqual, omit} from "@pg-mono/nodash";

import {resetPrivilegedOfferList} from "../../offer/list/actions/fetch_privileged_offer_list";
import {PRIVILEGE_OFFER_QUERY_PARAM_NAME} from "../../offer/list/constants/offer_list";
import {Country} from "../../region/types/Country";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {useUserSearchPreferences} from "../../user/hooks/use_user_search_preferences";
import {ILatestQuery} from "../../utils/latest_query";
import {ISearchInputValue} from "../components/ISearchInputValue";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {getStringQueryValuesFromFormValues} from "../utils/get_string_values_from_form_values";

interface IProps {
    formValues: SearchFormValuesType;
    latestQuery: ILatestQuery;
    resetPrivilegedOfferList: typeof resetPrivilegedOfferList;
    onSubmit: (formValues: SearchFormValuesType, userQueryParams?: {distance?: number}) => void;
}

const useSearchAndFilterUpdate = (props: IProps) => {
    const {saveUserPreferences} = useUserSearchPreferences({debounce: true});
    const lastUserSegmentRegionSelectionKey = useRef("");

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onAfterChange = async (name?: string, value?: any, userQueryParams?: any) => {
        if (name === "search" && value && typeof value === "object" && "tabType" in value) {
            const searchValue = value as ISearchInputValue;

            if (searchValue.tabType === SearchTab.Regions) {
                if (!searchValue.regions?.length) {
                    lastUserSegmentRegionSelectionKey.current = "";
                } else {
                    const selectionKey = [...searchValue.regions.map((r) => r.id)].sort((a, b) => a - b).join(",");

                    if (selectionKey !== lastUserSegmentRegionSelectionKey.current) {
                        lastUserSegmentRegionSelectionKey.current = selectionKey;

                        hitUserSegmentLocationEntry(searchValue.regions);
                    }
                }
            }
        }

        const queryValuesToOmit = [
            "sort",
            "page",
            "construction_end_date",
            "floor_choices",
            "house_type",
            "house_additional_areas",
            "house_storeys",
            "price_1",
            "instalment",
            PRIVILEGE_OFFER_QUERY_PARAM_NAME
        ];
        const lastQueryValuesToOmit = [
            "sort",
            "page",
            "price_lower_than_average",
            "is_luxury",
            "construction_end_date",
            "floor_choices",
            "house_type",
            "house_additional_areas",
            "house_storeys",
            "country",
            "region_name",
            "price_1",
            "instalment",
            "saveSearchResponse",
            PRIVILEGE_OFFER_QUERY_PARAM_NAME,
            //those params for some reason ended in url on moving from map to list on abroad offers, their presence in the link does not affect the content of the listing, omitted to avoid unnecessary url manipulation
            "display_type",
            "page_size",
            "for_sale",
            "show_on_listing",
            "limited_presentation"
        ] as (keyof ILatestQuery)[];

        if (props.latestQuery.country && props.latestQuery.country !== Country.POLAND.toString()) {
            queryValuesToOmit.push("distance");
            lastQueryValuesToOmit.push("distance");
        }

        const currentValues = name ? {...props.formValues, [name]: value} : props.formValues;
        const queryValues = getStringQueryValuesFromFormValues(currentValues);
        await saveUserPreferences({filters: queryValues});

        const isQueryEqual = isEqual(omit(queryValues, queryValuesToOmit), omit(props.latestQuery, lastQueryValuesToOmit));

        if (name && name === "search" && !!value.label) {
            debouncedSearchTypingHit({query: value.label});
        }

        //There is data mismatch between latestQuery and currentValues in construction_end_date and floor_choices
        //Simple omit in constant above will not work
        const isConstructionEndDateEqual = () => {
            if (props.latestQuery?.construction_end_date && currentValues.construction_end_date.length > 0) {
                return props.latestQuery.construction_end_date === currentValues.construction_end_date[0].toString();
            } else {
                return !props.latestQuery?.construction_end_date && currentValues.construction_end_date.length === 0;
            }
        };

        const isFloorEqual = () => {
            if (props.latestQuery?.floor_choices && currentValues.floor_choices?.length > 0) {
                const mappedLatestQueryFloorChoices =
                    typeof props.latestQuery.floor_choices === "string" ? [props.latestQuery.floor_choices] : [...props.latestQuery.floor_choices];
                const mappedCurrentFloorChoices = currentValues.floor_choices.map((floor: number) => floor.toString());

                return isEqual(mappedLatestQueryFloorChoices, mappedCurrentFloorChoices);
            } else {
                return (
                    (!props.latestQuery?.floor_choices && currentValues.floor_choices?.length === 0) ||
                    (!props.latestQuery?.floor_choices && !currentValues.floor_choices)
                );
            }
        };

        const areHouseFiltersEqual = () => {
            const latestQueryHouseType = props.latestQuery.house_type || "";
            const latestQueryHouseAdditionalAreas = props.latestQuery.house_additional_areas || [];
            const latestQueryHouseFloorOptions = props.latestQuery.house_storeys || "";
            const currentValuesHouseType = currentValues.house_type || "";
            const currentValuesHouseAdditionalAreas = currentValues.house_additional_areas || [];
            const currentValuesHouseFloorOptions = currentValues.house_storeys || "";

            // normalize arrays and numbers into strings eg `[1]` or `1` equals `"1"`
            return (
                latestQueryHouseType.toString() === currentValuesHouseType.toString() &&
                latestQueryHouseAdditionalAreas.toString() === currentValuesHouseAdditionalAreas.toString() &&
                latestQueryHouseFloorOptions.toString() === currentValuesHouseFloorOptions.toString()
            );
        };

        if (isQueryEqual && isConstructionEndDateEqual() && isFloorEqual() && areHouseFiltersEqual()) {
            return;
        }

        // We won't show privileged offers after filters have been changed
        props.resetPrivilegedOfferList();
        return props.onSubmit(currentValues, userQueryParams);
    };

    return {
        onAfterChange
    };
};

export default useSearchAndFilterUpdate;
import {useRef} from "react";

import {hitUserSegmentLocationEntry} from "@pg-mono/google-tag-manager";
import {isEqual, omit} from "@pg-mono/nodash";

import {resetPrivilegedOfferList} from "../../offer/list/actions/fetch_privileged_offer_list";
import {PRIVILEGE_OFFER_QUERY_PARAM_NAME} from "../../offer/list/constants/offer_list";
import {Country} from "../../region/types/Country";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {useUserSearchPreferences} from "../../user/hooks/use_user_search_preferences";
import {ILatestQuery} from "../../utils/latest_query";
import {ISearchInputValue} from "../components/ISearchInputValue";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {getStringQueryValuesFromFormValues} from "../utils/get_string_values_from_form_values";

interface IProps {
    formValues: SearchFormValuesType;
    latestQuery: ILatestQuery;
    resetPrivilegedOfferList: typeof resetPrivilegedOfferList;
    onSubmit: (formValues: SearchFormValuesType, userQueryParams?: {distance?: number}) => void;
}

const useSearchAndFilterUpdate = (props: IProps) => {
    const {saveUserPreferences} = useUserSearchPreferences({debounce: true});
    const lastUserSegmentRegionSelectionKey = useRef("");

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onAfterChange = async (name?: string, value?: any, userQueryParams?: any) => {
        if (name === "search" && value && typeof value === "object" && "tabType" in value) {
            const searchValue = value as ISearchInputValue;

            if (searchValue.tabType === SearchTab.Regions) {
                if (!searchValue.regions?.length) {
                    lastUserSegmentRegionSelectionKey.current = "";
                } else {
                    const selectionKey = [...searchValue.regions.map((r) => r.id)].sort((a, b) => a - b).join(",");

                    if (selectionKey !== lastUserSegmentRegionSelectionKey.current) {
                        lastUserSegmentRegionSelectionKey.current = selectionKey;

                        hitUserSegmentLocationEntry(searchValue.regions);
                    }
                }
            }
        }

        const queryValuesToOmit = [
            "sort",
            "page",
            "construction_end_date",
            "floor_choices",
            "house_type",
            "house_additional_areas",
            "house_storeys",
            "price_1",
            "instalment",
            PRIVILEGE_OFFER_QUERY_PARAM_NAME
        ];
        const lastQueryValuesToOmit = [
            "sort",
            "page",
            "price_lower_than_average",
            "is_luxury",
            "construction_end_date",
            "floor_choices",
            "house_type",
            "house_additional_areas",
            "house_storeys",
            "country",
            "region_name",
            "price_1",
            "instalment",
            "saveSearchResponse",
            PRIVILEGE_OFFER_QUERY_PARAM_NAME,
            //those params for some reason ended in url on moving from map to list on abroad offers, their presence in the link does not affect the content of the listing, omitted to avoid unnecessary url manipulation
            "display_type",
            "page_size",
            "for_sale",
            "show_on_listing",
            "limited_presentation"
        ] as (keyof ILatestQuery)[];

        if (props.latestQuery.country && props.latestQuery.country !== Country.POLAND.toString()) {
            queryValuesToOmit.push("distance");
            lastQueryValuesToOmit.push("distance");
        }

        const currentValues = name ? {...props.formValues, [name]: value} : props.formValues;
        const queryValues = getStringQueryValuesFromFormValues(currentValues);
        await saveUserPreferences({filters: queryValues});

        const isQueryEqual = isEqual(omit(queryValues, queryValuesToOmit), omit(props.latestQuery, lastQueryValuesToOmit));

        if (name && name === "search" && !!value.label) {
            debouncedSearchTypingHit({query: value.label});
        }

        //There is data mismatch between latestQuery and currentValues in construction_end_date and floor_choices
        //Simple omit in constant above will not work
        const isConstructionEndDateEqual = () => {
            if (props.latestQuery?.construction_end_date && currentValues.construction_end_date.length > 0) {
                return props.latestQuery.construction_end_date === currentValues.construction_end_date[0].toString();
            } else {
                return !props.latestQuery?.construction_end_date && currentValues.construction_end_date.length === 0;
            }
        };

        const isFloorEqual = () => {
            if (props.latestQuery?.floor_choices && currentValues.floor_choices?.length > 0) {
                const mappedLatestQueryFloorChoices =
                    typeof props.latestQuery.floor_choices === "string" ? [props.latestQuery.floor_choices] : [...props.latestQuery.floor_choices];
                const mappedCurrentFloorChoices = currentValues.floor_choices.map((floor: number) => floor.toString());

                return isEqual(mappedLatestQueryFloorChoices, mappedCurrentFloorChoices);
            } else {
                return (
                    (!props.latestQuery?.floor_choices && currentValues.floor_choices?.length === 0) ||
                    (!props.latestQuery?.floor_choices && !currentValues.floor_choices)
                );
            }
        };

        const areHouseFiltersEqual = () => {
            const latestQueryHouseType = props.latestQuery.house_type || "";
            const latestQueryHouseAdditionalAreas = props.latestQuery.house_additional_areas || [];
            const latestQueryHouseFloorOptions = props.latestQuery.house_storeys || "";
            const currentValuesHouseType = currentValues.house_type || "";
            const currentValuesHouseAdditionalAreas = currentValues.house_additional_areas || [];
            const currentValuesHouseFloorOptions = currentValues.house_storeys || "";

            // normalize arrays and numbers into strings eg `[1]` or `1` equals `"1"`
            return (
                latestQueryHouseType.toString() === currentValuesHouseType.toString() &&
                latestQueryHouseAdditionalAreas.toString() === currentValuesHouseAdditionalAreas.toString() &&
                latestQueryHouseFloorOptions.toString() === currentValuesHouseFloorOptions.toString()
            );
        };

        if (isQueryEqual && isConstructionEndDateEqual() && isFloorEqual() && areHouseFiltersEqual()) {
            return;
        }

        // We won't show privileged offers after filters have been changed
        props.resetPrivilegedOfferList();
        return props.onSubmit(currentValues, userQueryParams);
    };

    return {
        onAfterChange
    };
};

export default useSearchAndFilterUpdate;
